'use client';

import { MotionConfigContext, useInView, useReducedMotion } from 'motion/react';
import { useContext, useEffect, useRef, useState } from 'react';
import type { FloatingVoiceDockStatus } from '@/lib/floating-voice-dock-state';
import {
  VoiceCompanionFace,
  type VoiceAvatarLook,
} from './voice-companion-face';
import {
  getVoiceColor,
  voiceAccentColor,
  voiceBaseColorForSeed,
  voiceColorCss,
} from './voice-companion-colors';

export function VoiceCompanionAvatar({
  status,
  inputMode,
  micMuted,
  pttActive,
  look,
}: {
  status: FloatingVoiceDockStatus;
  inputMode: 'vad' | 'ptt';
  micMuted: boolean;
  pttActive: boolean;
  look: VoiceAvatarLook;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { amount: 0.5 });
  const reducedMotion = useReducedMotion();
  const motionConfig = useContext(MotionConfigContext);
  const [tabVisible, setTabVisible] = useState(true);

  useEffect(() => {
    const update = () => setTabVisible(document.visibilityState === 'visible');
    update();
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);

  const speaking = status === 'speaking';
  const moving =
    inView &&
    tabVisible &&
    !reducedMotion &&
    motionConfig.reducedMotion !== 'always';
  const listening = status === 'listening';
  const mode = speaking ? 'speaking' : listening ? 'listening' : 'idle';
  const voiceState = micMuted
    ? 'muted'
    : inputMode === 'ptt' && !pttActive && status === 'listening'
      ? 'waiting'
      : status;

  return (
    <span
      ref={ref}
      data-slot="voice-companion-face"
      data-voice-state={voiceState}
      className="relative flex size-16 items-center justify-center"
      style={{
        color: voiceColorCss(
          voiceAccentColor(
            getVoiceColor(mode, 0, voiceBaseColorForSeed(look.colorSeed))
          )
        ),
      }}
      aria-hidden="true"
    >
      <span className="relative flex size-16 items-center justify-center drop-shadow-sm">
        <VoiceCompanionFace mode={mode} running={moving} look={look} />
      </span>
      {speaking && (
        <svg
          viewBox="0 0 52 52"
          className="pointer-events-none absolute inset-0 size-16 overflow-visible"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          aria-hidden="true"
          opacity={0.7}
        >
          <path d="M4 18c-2 4-2 12 0 16M48 18c2 4 2 12 0 16" />
        </svg>
      )}
    </span>
  );
}
